import get from 'lodash/get'
import React, { useMemo } from 'react'
import type { Location } from 'react-router-dom'

import type { AddToDiscoUser } from 'types/user'

import LoginError from 'components/utils/login-error'

import Warning from '../warning'

type Props = {
  user: AddToDiscoUser
  location: Location
}

/**
 * Error message component for login form that displays authentication errors
 * Shows login errors, session expiration messages, and password strength context
 * @param user - User object containing error state and business information
 * @param location - Location object with query parameters (e.g., sessionExpired)
 */
const ErrorMessage = ({ user, location }: Props): React.ReactNode | null => {
  const { error, showLoginError } = user
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  if (!showLoginError && !error && !searchParams.get('sessionExpired'))
    return null
  const strength = get(
    user,
    'business.public_metadata.password_strength',
    'standard',
  )
  return (
    <Warning testId="login-form-error">
      {showLoginError && error ? (
        <LoginError passwordStrength={strength} error={error} />
      ) : (
        'Your session has expired. Please login again.'
      )}
    </Warning>
  )
}

export default ErrorMessage
